import React, { useState, useEffect } from 'react';
import { getAssistants, linkAssistant, unlinkAssistant } from '../services/apiService';
import { Assistant } from '../types';
import { useDebounce } from '../hooks/useDebounce';

/**
 * AssistantManagementPage Component - Manages assistant links for the authenticated user
 * 
 * This component allows users to:
 * - View all assistants currently linked to their account
 * - Link new assistants by entering the assistant ID
 * - Unlink existing assistants
 * - See success/error feedback for operations
 */
const AssistantManagementPage: React.FC = () => {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [newAssistantId, setNewAssistantId] = useState('');
  const [newAssistantName, setNewAssistantName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    fetchAssistants(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const fetchAssistants = async (search?: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAssistants(search);
      setAssistants(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch assistants';
      setError(errorMessage);
      console.error('Error fetching assistants:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newAssistantId.trim()) {
      setError('Please enter an assistant ID');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setSuccessMessage(null);
      
      await linkAssistant({
        vapi_assistant_id: newAssistantId.trim(),
        name: newAssistantName.trim() || undefined
      });
      
      setSuccessMessage('Assistant linked successfully!');
      setNewAssistantId('');
      setNewAssistantName('');
      
      // Refresh the assistants list
      await fetchAssistants();
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to link assistant';
      setError(errorMessage);
      console.error('Error linking assistant:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnlink = async (assistantId: string, assistantName?: string) => {
    if (!window.confirm(`Are you sure you want to unlink "${assistantName || 'this assistant'}"? This will hide all associated call logs.`)) {
      return;
    }

    try {
      setError(null);
      setSuccessMessage(null);
      
      await unlinkAssistant(assistantId);
      
      setSuccessMessage('Assistant unlinked successfully!');
      
      // Refresh the assistants list
      await fetchAssistants();
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to unlink assistant';
      setError(errorMessage);
      console.error('Error unlinking assistant:', err);
    }
  };

  // Clear messages after a delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Assistant Management</h1>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Linked Assistants Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Linked Assistants</h2>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500 text-lg">Loading assistants...</div>
          </div>
        ) : assistants.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-6 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No assistants linked</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by linking your first assistant below.</p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {assistants.map((assistant) => (
                <li key={assistant.vapi_assistant_id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-900">
                            {assistant.name || 'Unnamed Assistant'}
                          </p>
                          <p className="text-sm text-gray-500">
                            ID: {assistant.vapi_assistant_id}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 text-sm text-gray-500">
                        Linked {new Date(assistant.created_at).toLocaleDateString()}
                      </div>
                      <button
                        onClick={() => handleUnlink(assistant.vapi_assistant_id, assistant.name)}
                        className="text-red-600 hover:text-red-900 text-sm font-medium"
                      >
                        Unlink
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Link New Assistant Section */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Link a New Assistant</h2>
        
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="assistant-id" className="block text-sm font-medium text-gray-700">
                  Assistant ID *
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="assistant-id"
                    value={newAssistantId}
                    onChange={(e) => setNewAssistantId(e.target.value)}
                    placeholder="Enter your assistant ID"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    disabled={isSubmitting}
                    required
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  You can find your assistant ID in your voice assistant dashboard.
                </p>
              </div>

              <div>
                <label htmlFor="assistant-name" className="block text-sm font-medium text-gray-700">
                  Assistant Name (Optional)
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="assistant-name"
                    value={newAssistantName}
                    onChange={(e) => setNewAssistantName(e.target.value)}
                    placeholder="Enter a friendly name for this assistant"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    disabled={isSubmitting}
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Give your assistant a memorable name to easily identify it.
                </p>
              </div>
              
              <div>
                <button
                  type="submit"
                  disabled={isSubmitting || !newAssistantId.trim()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Linking...
                    </>
                  ) : (
                    'Link Assistant'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssistantManagementPage;
